import { Link, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { signOutLocally, getUserById } from "../services/userService"
import { useAuth } from "../hooks/useAuth"
import "react-responsive-modal/styles.css"
import { consoleLog } from "../utilities/helpers"
import { PROFILE_PICTURE_CHANGED_EVENT } from "../utilities/profilePicture"

export default function UserMenu({
    avatar,
    notificationsCount = 0,
    onShowNotifications,
}) {
    const navigate = useNavigate()
    const isLoggedIn = useAuth()
    const [userData, setUserData] = useState(null)
    const [isLoadingUser, setIsLoadingUser] = useState(false)

    // Fetch user data when logged in
    useEffect(() => {
        const fetchUserData = async () => {
            if (isLoggedIn) {
                setIsLoadingUser(true)
                try {
                    // Get user ID from localStorage (stored during login/registration)
                    const userDataStr =
                        localStorage.getItem("chaperon_user_data")
                    if (userDataStr) {
                        const storedUser = JSON.parse(userDataStr)

                        // If we have user ID, fetch fresh data from API
                        if (storedUser.id) {
                            consoleLog(
                                "Fetching user data for ID:",
                                storedUser.id,
                            )
                            const response = await getUserById(storedUser.id)
                            if (
                                response.status === "success" &&
                                response.user
                            ) {
                                setUserData(response.user)
                            } else {
                                // Fallback to stored data
                                setUserData(storedUser)
                            }
                        } else {
                            // Use stored data if no ID available
                            setUserData(storedUser)
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error)
                    // Try to use cached data from localStorage
                    const userDataStr =
                        localStorage.getItem("chaperon_user_data")
                    if (userDataStr) {
                        setUserData(JSON.parse(userDataStr))
                    }
                } finally {
                    setIsLoadingUser(false)
                }
            } else {
                setUserData(null)
            }
        }

        fetchUserData()
    }, [isLoggedIn])

    // The Settings page fires this after a new avatar upload
    useEffect(() => {
        const handleAvatarChanged = (event) => {
            setUserData((current) =>
                current
                    ? { ...current, profile_picture_url: event.detail?.url }
                    : current,
            )
        }
        window.addEventListener(
            PROFILE_PICTURE_CHANGED_EVENT,
            handleAvatarChanged,
        )
        return () =>
            window.removeEventListener(
                PROFILE_PICTURE_CHANGED_EVENT,
                handleAvatarChanged,
            )
    }, [])

    const handleLogout = () => {
        signOutLocally()
        setUserData(null)
        navigate("/")
    }

    return (
        <div className="d-flex justify-content-end" style={{ width: "100%" }}>
            {!isLoggedIn && (
                <div className="ps-3 pe-3 align-items-center flex-nowrap justify-content-end d-flex gap-2">
                    <Link to="/user/login" className="btn btn-dark">
                        Sign in
                    </Link>
                </div>
            )}
            {isLoggedIn && (
                <div className="flex-shrink-0 dropdown pe-5">
                    <div
                        className="d-flex align-items-center gap-2 dropdown-toggle"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        role="button"
                        style={{ cursor: "pointer" }}
                    >
                        {isLoadingUser ? (
                            <span className="text-muted">Loading...</span>
                        ) : userData?.username ? (
                            <span className="fw-medium text-dark">
                                <kbd>{userData.username}</kbd>
                            </span>
                        ) : null}
                        {consoleLog("User Data in UserMenu:", userData)}
                        {userData?.profile_picture_url ? (
                            <img
                                src={userData.profile_picture_url}
                                alt={userData?.username || "user"}
                                width="32"
                                height="32"
                                className="rounded-circle"
                            />
                        ) : (
                            <i
                                className="bi bi-person-circle"
                                style={{ fontSize: "32px" }}
                            ></i>
                        )}
                    </div>
                    <ul className="dropdown-menu text-small shadow">
                        <li>
                            <Link className="dropdown-item " to={`/dashboard`}>
                                Dashboard
                            </Link>
                        </li>
                        <li>
                            <Link className="dropdown-item " to={`/community`}>
                                Community
                            </Link>
                        </li>
                        <li>
                            <hr
                                className="dropdown-divider mx-auto"
                                style={{ width: "50%" }}
                            ></hr>
                        </li>
                        <li>
                            <Link
                                className="dropdown-item text-center"
                                to={`/community/checker`}
                            >
                                <i className="bi bi-heart-fill me-1"></i>{" "}
                                Checker
                            </Link>
                        </li>
                        <li>
                            <Link
                                className="dropdown-item text-center"
                                to={`/community/convoy`}
                            >
                                <i className="bi bi-eye-fill me-1"></i>{" "}
                                Convoy
                            </Link>
                        </li>
                        <li>
                            <hr className="dropdown-divider"></hr>
                        </li>
                        <li>
                            <Link className="dropdown-item " to={`/`}>
                                Profile
                            </Link>
                        </li>
                        <li>
                            <button
                                className="dropdown-item position-relative"
                                onClick={() => {
                                    if (onShowNotifications) {
                                        onShowNotifications()
                                    }
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    textAlign: "left",
                                    cursor: "pointer",
                                }}
                            >
                                Notifications
                                {notificationsCount > 0 && (
                                    <span className="position-absolute top-35 start-40 translate-middle badge rounded-pill bg-danger">
                                        {notificationsCount}
                                        <span className="visually-hidden">
                                            unread messages
                                        </span>
                                    </span>
                                )}
                            </button>
                        </li>
                        <li>
                            <Link className="dropdown-item" to="/settings">
                                Settings
                            </Link>
                        </li>
                        <li>
                            <Link className="dropdown-item" to="/contact">
                                Contact us
                            </Link>
                        </li>
                        <li>
                            <a
                                className="dropdown-item"
                                href="https://getchaperon.com/#access"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download apps
                            </a>
                        </li>
                        <li>
                            <Link
                                className="dropdown-item dropdown-item-upgrade"
                                to="/pricing"
                            >
                                <i className="bi bi-arrow-up-circle me-2"></i>
                                Upgrade
                            </Link>
                        </li>
                        <li>
                            <hr className="dropdown-divider"></hr>
                        </li>
                        <li>
                            <button
                                className="dropdown-item"
                                onClick={handleLogout}
                            >
                                Sign out
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    )
}
